/* eslint-disable class-methods-use-this */
/**
 * Simple Scroll Parallax
 * A lightweight parallax system that applies vertical translation based on scroll position
 */

class ScrollParallax {
  constructor(elements = [], options = {}) {
    this.elements = []
    this.isActive = false
    this.options = {
      onlyInView: false, // Only apply parallax when element is in viewport
      viewportOffset: 100, // Offset from viewport edge to start/stop parallax
      ...options,
    }

    // Define breakpoints based on your system
    this.breakpoints = {
      xs: 420,
      s: 640,
      m: 768,
      l: 1024,
      xl: 1220,
    }

    // Current breakpoint state
    this.currentBreakpoint = this.getCurrentBreakpoint()

    // Initialize elements
    elements.forEach((config) => {
      const element = this.getElement(config)
      if (element) {
        // Optimize element for smooth animation
        element.style.willChange = 'transform'
        element.style.backfaceVisibility = 'hidden'
        element.style.transform = 'translate3d(0, 0, 0)' // Initial GPU layer

        const elementConfig = {
          element,
          config, // Store original config for breakpoint calculations
          selector: config.selector || (config.id ? `#${config.id}` : ''),
          id: config.id || config.selector,
          onlyInView: config.onlyInView !== undefined ? config.onlyInView : this.options.onlyInView,
          viewportOffset: config.viewportOffset !== undefined ? config.viewportOffset : this.options.viewportOffset,
          isInView: false, // Track current viewport state
          lastOffset: 0, // Track last applied offset for smooth transitions
          currentMultiplier: 0, // Current active multiplier
        }

        // Calculate initial multiplier
        elementConfig.currentMultiplier = this.calculateMultiplier(config.multiplier, this.currentBreakpoint)

        this.elements.push(elementConfig)
      }
    })

    if (this.elements.length > 0) {
      this.init()
    }
  }

  /**
   * Get element using selector or id
   */
  getElement(config) {
    if (config.selector) return document.querySelector(config.selector)
    if (config.id) return document.getElementById(config.id)
    return null
  }

  /**
   * Get current breakpoint based on window width
   */
  getCurrentBreakpoint() {
    const width = window.innerWidth

    if (width >= this.breakpoints.xl) return 'xl'
    if (width >= this.breakpoints.l) return 'l'
    if (width >= this.breakpoints.m) return 'm'
    if (width >= this.breakpoints.s) return 's'
    return 'xs'
  }

  /**
   * Calculate multiplier for current breakpoint
   */
  calculateMultiplier(multiplierConfig, breakpoint) {
    if (typeof multiplierConfig === 'number') {
      return multiplierConfig
    }

    if (typeof multiplierConfig === 'object' && multiplierConfig !== null) {
      // Return exact match or fall back to previous breakpoint
      const breakpointOrder = ['xs', 's', 'm', 'l', 'xl']
      const currentIndex = breakpointOrder.indexOf(breakpoint)

      // Look for current breakpoint or fall back to previous ones
      for (let i = currentIndex; i >= 0; i--) {
        const bp = breakpointOrder[i]
        if (multiplierConfig[bp] !== undefined) {
          return multiplierConfig[bp]
        }
      }

      // If no match found, return 0
      return 0
    }

    return 0
  }

  init() {
    // Smooth RAF-based scroll handling
    let ticking = false

    this.handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateElements()
          ticking = false
        })
        ticking = true
      }
    }

    // Handle breakpoint changes on resize
    this.handleResize = () => {
      const newBreakpoint = this.getCurrentBreakpoint()
      if (newBreakpoint !== this.currentBreakpoint) {
        this.currentBreakpoint = newBreakpoint
        this.updateBreakpoints()
      }
    }

    // Start listening
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize, { passive: true })
    this.isActive = true

    // Initial update
    this.updateElements()
  }

  /**
   * Update all element multipliers when breakpoint changes
   */
  updateBreakpoints() {
    this.elements.forEach((elementConfig) => {
      const newMultiplier = this.calculateMultiplier(elementConfig.config.multiplier, this.currentBreakpoint)
      elementConfig.currentMultiplier = newMultiplier
    })

    // Force immediate update
    this.updateElements()
  }

  updateElements() {
    const scrollY = window.pageYOffset
    const viewportHeight = window.innerHeight

    this.elements.forEach((elementConfig) => {
      const { element, currentMultiplier, id, onlyInView, viewportOffset } = elementConfig

      // Skip processing if multiplier is 0 (disabled)
      if (currentMultiplier === 0) {
        // Reset transform if element was previously animated
        if (elementConfig.lastOffset !== 0) {
          element.style.transform = 'translate3d(0, 0, 0)'
          elementConfig.lastOffset = 0
        }
        return
      }

      let shouldApplyParallax = true

      // Check viewport visibility if onlyInView is enabled
      if (onlyInView) {
        const rect = element.getBoundingClientRect()
        const elementTop = rect.top + scrollY
        const elementBottom = elementTop + rect.height

        // Check if element is in viewport with offset
        const viewportTop = scrollY - viewportOffset
        const viewportBottom = scrollY + viewportHeight + viewportOffset

        elementConfig.isInView = elementBottom > viewportTop && elementTop < viewportBottom

        // Only apply parallax if element is in view
        shouldApplyParallax = elementConfig.isInView
      }

      if (shouldApplyParallax) {
        // Calculate offset based on scroll position and current multiplier
        const offset = scrollY * currentMultiplier
        elementConfig.lastOffset = offset

        // Apply transform
        element.style.transform = `translate3d(0, ${offset}px, 0)`
      } else if (onlyInView && !elementConfig.isInView) {
        // When element goes out of view, maintain last known offset to prevent jumping
        element.style.transform = `translate3d(0, ${elementConfig.lastOffset}px, 0)`
      }
    })
  }

  destroy() {
    if (this.isActive) {
      window.removeEventListener('scroll', this.handleScroll)
      window.removeEventListener('resize', this.handleResize)
      this.isActive = false

      // Reset elements
      this.elements.forEach(({ element }) => {
        element.style.transform = ''
        element.style.willChange = ''
        element.style.backfaceVisibility = ''
      })
    }
  }

  // Add element dynamically (updated for new system)
  addElement(config) {
    const element = this.getElement(config)
    if (element) {
      element.style.willChange = 'transform'
      element.style.backfaceVisibility = 'hidden'
      element.style.transform = 'translate3d(0, 0, 0)'

      const elementConfig = {
        element,
        config, // Store full config for breakpoint calculations
        selector: config.selector || (config.id ? `#${config.id}` : ''),
        id: config.id || config.selector,
        onlyInView: config.onlyInView !== undefined ? config.onlyInView : this.options.onlyInView,
        viewportOffset: config.viewportOffset !== undefined ? config.viewportOffset : this.options.viewportOffset,
        isInView: false,
        lastOffset: 0,
        currentMultiplier: this.calculateMultiplier(config.multiplier, this.currentBreakpoint),
      }

      this.elements.push(elementConfig)
    }
  }

  // Update multiplier for existing element
  updateMultiplier(id, newMultiplier) {
    const elementConfig = this.elements.find((el) => el.id === id)
    if (elementConfig) {
      elementConfig.config.multiplier = newMultiplier
      elementConfig.currentMultiplier = this.calculateMultiplier(newMultiplier, this.currentBreakpoint)
    }
  }
}

export default ScrollParallax
